import { ABSOLUTE_SIZE_REGEX, RELATIVE_SIZE_REGEX } from "./toolbar/column-width-modal/column-width.js";

const COMMIT_CHARACTERS = ["c", "l", "r", "p", "m", "b", ">"];

function parseArgument(spec, startIndex) {
    if (spec.charAt(startIndex) !== "{") {
        throw new Error("Missing opening brace");
    }
    let depth = 0;
    for (let i = startIndex; i < spec.length; i += 1) {
        if (spec.charAt(i) === "{") {
            depth += 1;
        } else if (spec.charAt(i) === "}") {
            depth -= 1;
        }
        if (depth === 0) {
            return i;
        }
    }
    throw new Error("Missing closing brace");
}

export function parseColumnSpecifications(specification) {
    const columns = [];
    let currentAlignment;
    let currentBorderLeft = 0;
    let currentBorderRight = 0;
    let currentContent = "";
    let currentCellSpacingLeft = "";
    let currentCellSpacingRight = "";
    let currentCustomCellDefinition = "";
    let currentIsParagraphColumn = false;
    let currentSize;

    function maybeCommit() {
        if (currentAlignment !== undefined) {
            columns.push({
                alignment: currentAlignment,
                borderLeft: currentBorderLeft,
                borderRight: currentBorderRight,
                content: currentContent,
                cellSpacingLeft: currentCellSpacingLeft,
                cellSpacingRight: currentCellSpacingRight,
                customCellDefinition: currentCustomCellDefinition,
                isParagraphColumn: currentIsParagraphColumn,
                size: currentSize,
            });
            currentAlignment = undefined;
            currentBorderLeft = 0;
            currentBorderRight = 0;
            currentContent = "";
            currentCellSpacingLeft = "";
            currentCellSpacingRight = "";
            currentCustomCellDefinition = "";
            currentIsParagraphColumn = false;
            currentSize = undefined;
        }
    }

    for (let i = 0; i < specification.length; i += 1) {
        const char = specification.charAt(i);
        if (COMMIT_CHARACTERS.includes(char)) {
            maybeCommit();
        }
        const hasAlignment = currentAlignment !== undefined;
        switch (char) {
            case "|":
                if (hasAlignment) {
                    currentBorderRight += 1;
                } else {
                    currentBorderLeft += 1;
                }
                break;
            case "c":
                currentAlignment = "center";
                currentContent += "c";
                break;
            case "l":
                currentAlignment = "left";
                currentContent += "l";
                break;
            case "r":
                currentAlignment = "right";
                currentContent += "r";
                break;
            case "p":
            case "m":
            case "b":
                currentIsParagraphColumn = true;
                currentAlignment = "paragraph";
                if (currentCustomCellDefinition !== "") {
                    const match = currentCustomCellDefinition.match(
                        />\{\s*\\(raggedleft|raggedright|centering)\s*\\arraybackslash\s*\}/,
                    );
                    if (match) {
                        if (match[1] === "raggedleft") {
                            currentAlignment = "right";
                        } else if (match[1] === "raggedright") {
                            currentAlignment = "left";
                        } else if (match[1] === "centering") {
                            currentAlignment = "center";
                        }
                    }
                }
                currentContent += char;
                {
                    const argumentEnd = parseArgument(specification, i + 1);
                    const columnDefinition = specification.slice(i, argumentEnd + 1);
                    const absoluteSizeMatch = columnDefinition.match(ABSOLUTE_SIZE_REGEX);
                    const relativeSizeMatch = columnDefinition.match(RELATIVE_SIZE_REGEX);
                    if (absoluteSizeMatch) {
                        currentSize = {
                            unit: absoluteSizeMatch[2],
                            width: parseFloat(absoluteSizeMatch[1]),
                        };
                    } else if (relativeSizeMatch) {
                        const widthAsFraction = parseFloat(relativeSizeMatch[1]) || 0;
                        currentSize = {
                            unit: "%",
                            width: widthAsFraction * 100,
                            command: relativeSizeMatch[2],
                        };
                    } else {
                        currentSize = {
                            unit: "custom",
                            width: columnDefinition.slice(2, -1),
                        };
                    }
                    currentContent += columnDefinition.slice(1);
                    i = argumentEnd;
                }
                break;
            case "@":
            case "!":
                {
                    const argumentEnd = parseArgument(specification, i + 1);
                    const argument = specification.slice(i, argumentEnd + 1);
                    i = argumentEnd;
                    if (hasAlignment) {
                        currentCellSpacingRight = argument;
                    } else {
                        currentCellSpacingLeft = argument;
                    }
                }
                break;
            case ">":
                {
                    const argumentEnd = parseArgument(specification, i + 1);
                    currentCustomCellDefinition = specification.slice(i, argumentEnd + 1);
                    i = argumentEnd;
                }
                break;
            case " ":
            case "\n":
            case "\t":
                currentContent += char;
                break;
            default:
                break;
        }
    }

    maybeCommit();
    return columns;
}

const isRowSeparator = (node) =>
    node.type.is("Command") && Boolean(node.getChild("KnownCtrlSym")?.getChild("LineBreak"));
const isKnownHLine = (node) =>
    node.type.is("Command") && Boolean(node.getChild("KnownCommand")?.getChild("HorizontalLine"));
const isMultiColumn = (node) =>
    node.type.is("Command") && Boolean(node.getChild("KnownCommand")?.getChild("MultiColumn"));
const isCmidRule = (node, state) =>
    node.type.is("Command") && state.sliceDoc(node.from, node.to).trim().startsWith("\\cmidrule");

function isHLine(node, state) {
    if (isKnownHLine(node)) {
        return true;
    }

    if (!node.type.is("Command")) {
        return false;
    }

    const commandText = state.sliceDoc(node.from, node.to).trim();
    return commandText === "\\botrule" || commandText === "\\bottomrule";
}

function parseTabularBody(node, state) {
    const body = {
        rows: [
            {
                cells: [],
                hlines: [],
                cellSeparators: [],
                position: { from: node.from, to: node.from },
            },
        ],
        rowSeparators: [],
    };

    const getLastRow = () => body.rows[body.rows.length - 1];
    const getLastCell = () => getLastRow().cells[getLastRow().cells.length - 1];

    getLastRow().cells.push({ content: "", position: { from: node.from, to: node.from } });

    for (let currentChild = node.firstChild; currentChild; currentChild = currentChild.nextSibling) {
        if (isRowSeparator(currentChild)) {
            const lastRow = getLastRow();
            body.rows.push({
                cells: [],
                hlines: [],
                cellSeparators: [],
                position: { from: currentChild.to, to: currentChild.to },
            });
            lastRow.position.to = currentChild.to;
            body.rowSeparators.push({ from: currentChild.from, to: currentChild.to });
            getLastRow().cells.push({ content: "", position: { from: currentChild.to, to: currentChild.to } });
            continue;
        }

        if (currentChild.type.is("Ampersand")) {
            getLastRow().cells.push({ content: "", position: { from: currentChild.to, to: currentChild.to } });
            getLastRow().cellSeparators.push({ from: currentChild.from, to: currentChild.to });
        } else if (isMultiColumn(currentChild)) {
            const multiColumn = currentChild.getChild("KnownCommand")?.getChild("MultiColumn");
            const columnArgument = multiColumn
                ?.getChild("ColumnArgument")
                ?.getChild("ShortTextArgument")
                ?.getChild("ShortArg");
            const spanArgument = multiColumn
                ?.getChild("SpanArgument")
                ?.getChild("ShortTextArgument")
                ?.getChild("ShortArg");
            const tabularArgument = multiColumn?.getChild("TabularArgument")?.getChild("TabularContent");
            if (!columnArgument || !spanArgument || !tabularArgument) {
                throw new Error("Invalid multicolumn definition");
            }
            const columnSpecification = parseColumnSpecifications(
                state.sliceDoc(columnArgument.from, columnArgument.to),
            );
            const span = parseInt(state.sliceDoc(spanArgument.from, spanArgument.to), 10);
            const cellContent = state.sliceDoc(tabularArgument.from, tabularArgument.to);
            const lastCell = getLastCell();
            lastCell.multiColumn = {
                columnSpecification: {
                    position: { from: columnArgument.from, to: columnArgument.to },
                    specification: columnSpecification,
                },
                span,
                preamble: { from: currentChild.from, to: tabularArgument.from },
                postamble: { from: tabularArgument.to, to: currentChild.to },
                position: { from: currentChild.from, to: currentChild.to },
            };
            lastCell.content = cellContent;
            lastCell.position.from = tabularArgument.from;
            lastCell.position.to = tabularArgument.to;
            continue;
        } else if (
            currentChild.type.is("NewLine") ||
            currentChild.type.is("Whitespace") ||
            currentChild.type.is("Comment") ||
            currentChild.type.is("BlankLine")
        ) {
            const lastCell = getLastCell();
            if (!lastCell?.multiColumn) {
                if (lastCell.content.trim() === "") {
                    lastCell.position.from = currentChild.to;
                    lastCell.position.to = currentChild.to;
                } else {
                    lastCell.content += state.sliceDoc(currentChild.from, currentChild.to);
                    lastCell.position.to = currentChild.to;
                }
            }
        } else if (isHLine(currentChild, state)) {
            const lastCell = getLastCell();
            if (lastCell) {
                lastCell.position.from = currentChild.to;
                lastCell.position.to = currentChild.to;
            }
            getLastRow().hlines.push({ position: { from: currentChild.from, to: currentChild.to }, atBottom: false });
        } else if (isCmidRule(currentChild, state)) {
            const lastCell = getLastCell();
            if (lastCell && lastCell.content.trim() === "") {
                lastCell.position.from = currentChild.to;
                lastCell.position.to = currentChild.to;
            }
        } else {
            const lastCell = getLastCell();
            lastCell.content += state.sliceDoc(currentChild.from, currentChild.to);
            lastCell.position.to = currentChild.to;
        }
        getLastRow().position.to = currentChild.to;
    }

    const lastRow = getLastRow();
    if (body.rows.length > 1 && lastRow.cells.length === 1 && lastRow.cells[0].content.trim() === "") {
        const hlines = lastRow.hlines.map((hline) => ({ ...hline, atBottom: true }));
        body.rows.pop();
        getLastRow().hlines.push(...hlines);
        const lastLineContents = state.sliceDoc(lastRow.position.from, lastRow.position.to);
        const offset = lastLineContents.length - lastLineContents.trimEnd().length;
        getLastRow().position.to = lastRow.position.to - offset;
    }

    return body;
}

export function generateTable(node, state) {
    const beginEnv = node.getChild("BeginEnv");
    const textArguments = [];
    for (let child = beginEnv?.firstChild; child; child = child.nextSibling) {
        if (child.type.is("TextArgument")) {
            const longArg = child.getChild("LongArg");
            if (longArg) {
                textArguments.push(longArg);
            }
        }
    }
    const specification = textArguments[textArguments.length - 1];
    if (!specification) {
        throw new Error("Missing column specification");
    }

    const columns = parseColumnSpecifications(state.sliceDoc(specification.from, specification.to));
    const content = node.getChild("Content");
    const body = content?.getChild("TabularContent") ?? content?.getChild("Text") ?? content;
    if (!body) {
        throw new Error("Missing table body");
    }

    const tableData = parseTabularBody(body, state);
    const cellPositions = tableData.rows.map((row) =>
        row.cells.map((cell) => cell.multiColumn?.position ?? cell.position),
    );
    const cellSeparators = tableData.rows.map((row) => row.cellSeparators);
    const rowPositions = tableData.rows.map((row) => ({
        ...row.position,
        hlines: row.hlines.map((hline) => hline.position),
    }));
    const rows = tableData.rows.map((row) => ({
        cells: row.cells.map((cell) => {
            const cellData = {
                content: cell.content,
                from: cell.position.from,
                to: cell.position.to,
            };
            if (cell.multiColumn) {
                cellData.multiColumn = {
                    columns: {
                        specification: cell.multiColumn.columnSpecification.specification,
                        from: cell.multiColumn.columnSpecification.position.from,
                        to: cell.multiColumn.columnSpecification.position.to,
                    },
                    columnSpan: cell.multiColumn.span,
                    from: cell.multiColumn.position.from,
                    to: cell.multiColumn.position.to,
                    preamble: { from: cell.multiColumn.preamble.from, to: cell.multiColumn.preamble.to },
                    postamble: { from: cell.multiColumn.postamble.from, to: cell.multiColumn.postamble.to },
                };
            }
            return cellData;
        }),
        borderTop: row.hlines.filter((hline) => !hline.atBottom).length,
        borderBottom: row.hlines.filter((hline) => hline.atBottom).length,
    }));

    const table = new TableData(rows, columns);
    return {
        table,
        cellPositions,
        specification,
        rowPositions,
        rowSeparators: tableData.rowSeparators,
        cellSeparators,
    };
}

export function validateParsedTable(parseResult) {
    for (const row of parseResult.table.rows) {
        const rowLength = row.cells.reduce((acc, cell) => acc + (cell.multiColumn?.columnSpan ?? 1), 0);
        for (const cell of row.cells) {
            if (cell.multiColumn?.columns.specification && cell.multiColumn.columns.specification.length !== 1) {
                return false;
            }
        }
        if (rowLength !== parseResult.table.columns.length) {
            return false;
        }
    }
    return true;
}

export function parseTableEnvironment(tableNode) {
    const tableEnvironment = {
        table: { from: tableNode.from, to: tableNode.to },
    };

    tableNode.cursor().iterate(({ type, from, to }) => {
        if (tableEnvironment.caption && tableEnvironment.label) {
            return false;
        }
        if (type.is("Caption")) {
            tableEnvironment.caption = { from, to };
        } else if (type.is("Label")) {
            tableEnvironment.label = { from, to };
        }
    });

    return tableEnvironment;
}

export class TableData {
    constructor(rows, columns) {
        this.rows = rows;
        this.columns = columns;
    }

    getCellIndex(row, column) {
        let cellIndex = 0;
        for (let i = 0; i < this.rows[row].cells.length; i += 1) {
            cellIndex += this.rows[row].cells[i].multiColumn?.columnSpan ?? 1;
            if (column < cellIndex) {
                return i;
            }
        }
        return this.rows[row].cells.length - 1;
    }

    iterateCells(minRow, maxRow, minColumn, maxColumn, callback) {
        for (let row = minRow; row <= maxRow; row += 1) {
            let currentCellOffset = this.getCellBoundaries(row, minColumn).from;
            const minX = this.getCellIndex(row, minColumn);
            const maxX = this.getCellIndex(row, maxColumn);
            for (let column = minX; column <= maxX; column += 1) {
                const currentCell = this.rows[row].cells[column];
                const skip = currentCell.multiColumn?.columnSpan ?? 1;
                callback(currentCell, row, currentCellOffset);
                currentCellOffset += skip;
            }
        }
    }

    iterateSelection(selection, callback) {
        const { minX, maxX, minY, maxY } = selection.normalized();
        this.iterateCells(minY, maxY, minX, maxX, callback);
    }

    getCell(row, column) {
        return this.rows[row].cells[this.getCellIndex(row, column)];
    }

    getCellBoundaries(row, cell) {
        let currentCellOffset = 0;
        for (let index = 0; index < this.rows[row].cells.length; index += 1) {
            const currentCell = this.rows[row].cells[index];
            const skip = currentCell.multiColumn?.columnSpan ?? 1;
            if (currentCellOffset + skip > cell) {
                return { from: currentCellOffset, to: currentCellOffset + skip - 1 };
            }
            currentCellOffset += skip;
        }
        throw new Error("Couldn't find cell boundaries");
    }

    getBorderTheme() {
        if (this.rows.length === 0 || this.columns.length === 0) {
            return null;
        }

        const hasTopRule = this.rows[0].borderTop === 1;
        const hasMidRule =
            this.rows[1]?.borderTop === 1 && (this.rows.length === 2 || this.rows[1]?.borderBottom === 0);
        const hasBottomRule =
            this.rows[this.rows.length - 1].borderBottom === 1 &&
            (this.rows.length === 2 || this.rows[this.rows.length - 1].borderTop === 0);

        if (hasTopRule && hasMidRule && hasBottomRule) {
            let isBooktabs = true;
            for (const row of this.rows.slice(2, -1)) {
                if (row.borderTop > 0 || row.borderBottom > 0) {
                    isBooktabs = false;
                }
            }
            for (const column of this.columns) {
                if (column.borderLeft > 0 || column.borderRight > 0) {
                    isBooktabs = false;
                }
            }
            if (isBooktabs) {
                return 2;
            }
        }

        const hasBottomBorder = this.rows[this.rows.length - 1].borderBottom > 0;
        const hasLeftBorder = this.columns[0].borderLeft > 0;

        for (const row of this.rows) {
            if (hasBottomBorder === (row.borderTop === 0)) {
                return null;
            }
        }

        for (const column of this.columns) {
            if (hasLeftBorder === (column.borderRight === 0)) {
                return null;
            }
        }

        for (const row of this.rows) {
            for (const cell of row.cells) {
                if (cell.multiColumn) {
                    if (cell.multiColumn.columns.specification.length === 0) {
                        return null;
                    }
                    const firstCell = cell.multiColumn.columns.specification[0];
                    if (hasLeftBorder === (firstCell.borderLeft === 0)) {
                        return null;
                    }
                    for (const column of cell.multiColumn.columns.specification) {
                        if (hasLeftBorder === (column.borderRight === 0)) {
                            return null;
                        }
                    }
                }
            }
        }

        if (hasBottomBorder && hasLeftBorder) {
            return 1;
        }
        if (!hasBottomBorder && !hasLeftBorder) {
            return 0;
        }
        return null;
    }
}
